import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './AppDetails.css';

interface GameStep {
  id: string;
  description: string;
  credits: number;
  completed: boolean;
}

interface GameOffer {
  id: string;
  name: string;
  description: string;
  icon: string;
  steps: GameStep[];
}

const gameDetails: { [key: string]: GameOffer } = {
  'candy-crush': {
    id: 'candy-crush',
    name: 'Candy Crush Saga',
    description: 'Match 3 candy puzzles in this sweet and addictive game! Progress through hundreds of levels and become a candy crushing champion.',
    icon: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/64/c3/76/64c37621-2f23-b819-1f87-f12437d11ca2/AppIcon-0-0-1x_U007emarketing-0-8-0-85-220.png/460x0w.png',
    steps: [
      { id: '1', description: 'Complete level 10', credits: 50, completed: false },
      { id: '2', description: 'Reach level 25', credits: 100, completed: false },
      { id: '3', description: 'Achieve level 50', credits: 200, completed: false }
    ]
  },
  'clash-royale': {
    id: 'clash-royale',
    name: 'Clash Royale',
    description: 'Enter the Arena! Build your battle deck and engage in real-time battles against players from around the world.',
    icon: 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/96/db/3e/96db3e17-52e8-f5e0-922a-8cb706f70332/AppIcon-0-0-1x_U007emarketing-0-7-0-85-220.png/460x0w.png',
    steps: [
      { id: '1', description: 'Win 5 battles', credits: 75, completed: true },
      { id: '2', description: 'Reach Arena 3', credits: 150, completed: false },
      { id: '3', description: 'Unlock Arena 5', credits: 250, completed: false },
      { id: '4', description: 'Join a clan', credits: 50, completed: false }
    ]
  },
  'pokemon-go': {
    id: 'pokemon-go',
    name: 'Pokémon GO',
    description: 'Explore the real world and catch Pokémon! Travel between the real world and the virtual world of Pokémon.',
    icon: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/2b/1d/fa/2b1dfa15-2f75-866a-c1e9-b9557a870fd1/AppIcon-0-0-1x_U007emarketing-0-8-0-85-220.png/460x0w.png',
    steps: [
      { id: '1', description: 'Catch 10 Pokémon', credits: 100, completed: false },
      { id: '2', description: 'Catch 20 different species', credits: 200, completed: false },
      { id: '3', description: 'Reach trainer level 10', credits: 150, completed: false },
      { id: '4', description: 'Win 3 gym battles', credits: 100, completed: false }
    ]
  },
  'subway-surfers': {
    id: 'subway-surfers',
    name: 'Subway Surfers',
    description: 'Dash as fast as you can! Dodge the oncoming trains and help Jake escape from the grumpy Inspector.',
    icon: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/2a/20/5e/2a205e9c-38c2-25b1-edf7-24e4fc12124a/AppIcon-0-0-1x_U007emarketing-0-8-0-85-220.png/460x0w.png',
    steps: [
      { id: '1', description: 'Score 100,000 points', credits: 50, completed: false },
      { id: '2', description: 'Score 500,000 points', credits: 100, completed: false },
      { id: '3', description: 'Collect 50 coins in one run', credits: 75, completed: false }
    ]
  },
  'roblox': {
    id: 'roblox',
    name: 'Roblox',
    description: 'Join millions of people and discover an infinite variety of immersive experiences created by a global community!',
    icon: 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/4d/f0/d0/4df0d037-96b0-1f9b-1dc7-13fdacd701d1/AppIcon-0-0-1x_U007epad-0-1-0-85-220.png/460x0w.png',
    steps: [
      { id: '1', description: 'Play for 10 minutes', credits: 50, completed: true },
      { id: '2', description: 'Join 3 different games', credits: 100, completed: true },
      { id: '3', description: 'Play for 30 minutes total', credits: 150, completed: false },
      { id: '4', description: 'Add 2 friends', credits: 75, completed: false },
      { id: '5', description: 'Customize your avatar', credits: 50, completed: false }
    ]
  }
};

const AppDetails: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();

  const game = appId ? gameDetails[appId] : null;

  if (!game) {
    return (
      <div className="app-details-container">
        <div className="app-details-error">
          <h2>Game not found</h2>
          <button onClick={() => navigate(-1)} className="back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handleStepClick = (step: GameStep) => {
    if (!step.completed) {
      console.log(`Claiming ${step.credits} credits for: ${step.description}`);
      // In a real app, this would make an API call to claim credits
    }
  };

  const inProgressSteps = game.steps.filter(s => s.completed).length;
  const progressPercentage = (inProgressSteps / game.steps.length) * 100;

  return (
    <div className="app-details-container">
      <button onClick={() => navigate(-1)} className="back-button">
        ← Back
      </button>

      <div className="app-details-header">
        <img src={game.icon} alt={game.name} className="app-details-icon" />
        <h1 className="app-details-title">{game.name}</h1>
        <p className="app-details-description">{game.description}</p>

        {progressPercentage > 0 && (
          <div className="app-progress-container">
            <div className="app-progress-bar">
              <div
                className="app-progress-fill"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span className="app-progress-text">
              {inProgressSteps}/{game.steps.length} completed
            </span>
          </div>
        )}
      </div>

      <div className="app-steps-section">
        <h2 className="steps-title">Complete tasks to earn credits 💗</h2>
        <div className="tracking-reminder-chip">
          <span className="chip-icon">💡</span>
          <span className="chip-text">Don't forget to enable tracking when prompted</span>
        </div>
        <div className="app-steps-list">
          {game.steps.map((step, index) => (
            <div
              key={step.id}
              className={`app-step ${step.completed ? 'completed' : ''}`}
            >
              <div className="step-number">{index + 1}</div>
              <div className="step-content">
                <div className="step-row">
                  <p className="step-description">{step.description}</p>
                  <button
                    className={`step-credit-button ${step.completed ? 'claimed' : ''}`}
                    onClick={() => handleStepClick(step)}
                    disabled={step.completed}
                  >
                    {step.completed ? '✓ Claimed' : `+${step.credits} credits`}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AppDetails;